// ============================================================================
// RÉGLAGES → PARAMÈTRES — les paramètres de CAISSE, puis la messagerie du back-office.
//
// ⚠️ CE QUE CET ÉCRAN PORTE A CHANGÉ (2026-09-06). La maquette range sous « Paramètres » les réglages
// que les CAISSES appliquent ; cet écran ne portait que la messagerie du back-office, faute d'un
// ailleurs — son en-tête disait « pour l'instant ». Les paramètres de caisse arrivent donc en tête, et
// la messagerie reste en dessous, sous un titre qui dit à qui elle appartient : deux responsables
// distincts (le commerçant / l'éditeur) ne se lisent pas dans le même bloc.
// ============================================================================

// ============================================================================
// LA MESSAGERIE SORTANTE DU BACK-OFFICE.
//
// POURQUOI CET ÉCRAN EXISTE (demande utilisateur 2026-08-30) : « l'utilisateur au BO doit pouvoir
// paramétrer un compte email via SMTP ou avoir la possibilité de faire le Mailjet ». Le back-office
// était la seule app à ne se régler que par son `.env` — et sur la bêta, `MAIL_TRANSPORT` était VIDE :
// le « mot de passe oublié » était éteint sans que personne puisse le rallumer autrement qu'en SSH.
//
// ⚠️ CE QUI EST AFFICHÉ EST CE QUI S'APPLIQUE, pas ce qui est enregistré. La pastille « en vigueur »
// dit la SOURCE effective (cet écran / la configuration serveur / rien) : un réglage incomplet laisse
// le `.env` reprendre la main, et l'écran doit le dire plutôt que de laisser croire qu'il pilote (§23.0).
// ⚠️ LE SECRET NE REDESCEND JAMAIS. On ne peut que le remplacer, jamais le relire : un champ mot de
// passe pré-rempli, même masqué, est un secret rendu au navigateur.
// ⚠️ L'ENVOI DE TEST EST L'ESSENTIEL de cette page — un réglage enregistré n'est pas un réglage qui
// marche. L'échec rend le message RÉEL du transport (« auth failed », « ENOTFOUND ») : c'est lui qui
// dit quoi corriger.
// ============================================================================
import { useEffect, useId, useState, type CSSProperties } from 'react';
import { alert, showToast, Trans } from '@orgazone/ui-web';
import { api, type MailSettings } from '../api';
import { ParametresCaisse } from './parametres-caisse';
import { t } from '../i18n';

export function Parametres() {
  return (
    <div>
      <ParametresCaisse />
      {/* ⚠️ SÉPARÉE ET TITRÉE : ces réglages-ci ne descendent à AUCUNE caisse — ils règlent le
          back-office lui-même. Les mêler aux paramètres de caisse laisserait croire qu'ils
          voyagent, et le premier réflexe serait de les chercher sur la tablette. */}
      <div style={{ marginTop: 34, paddingTop: 22, borderTop: '1px solid var(--ink-200)' }}>
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--t-h2)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>{t('Réglages du back-office')}</h2>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
            {t('Ceux qui ne descendent à aucune caisse — ils règlent cet outil, pas le magasin.')}
          </p>
        </div>
        <CarteMessagerie />
      </div>
    </div>
  );
}

function CarteMessagerie() {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [data, setData] = useState<MailSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState<Partial<MailSettings> & { secret?: string }>({});
  const [testTo, setTestTo] = useState('');
  const [testRes, setTestRes] = useState<{ ok: boolean; error?: string } | null>(null);

  const load = () => api.mailSettings()
    .then((r) => { setData(r); setF({ ...r, secret: '' }); setErr(null); })
    .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  useEffect(() => { void load(); }, []);

  const save = async () => {
    setBusy(true); setTestRes(null);
    try {
      const r = await api.updateMailSettings({
        transport: f.transport ?? 'none', fromEmail: f.fromEmail ?? null, fromName: f.fromName ?? null,
        smtpHost: f.smtpHost ?? null, smtpPort: f.smtpPort ?? null, smtpSecure: f.smtpSecure !== false,
        smtpUser: f.smtpUser ?? null, apiKey: f.apiKey ?? null,
        // ⚠️ Le secret n'est envoyé QUE s'il a été saisi : un champ vide veut dire « inchangé », jamais
        //    « efface-le » — sans quoi enregistrer un simple changement de port perdrait le mot de passe.
        ...(f.secret ? { secret: f.secret } : {}),
      });
      setData(r); setF({ ...r, secret: '' });
      showToast(t('Messagerie enregistrée.'));
    } catch (e) {
      await alert({ title: t('Réglage refusé'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  const tester = async () => {
    setBusy(true); setTestRes(null);
    try { setTestRes(await api.testMail(testTo.trim())); }
    catch (e) { setTestRes({ ok: false, error: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  if (err !== null) {
    return (
      <div className="dcard" style={{ padding: 18 }}>
        <p role="alert" style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--danger-ink)' }}>
          {t('Les réglages de messagerie n’ont pas pu être lus : {err}', { err })}
        </p>
      </div>
    );
  }
  if (!data) return <div className="dcard" style={{ padding: 18, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture…')}</div>;

  const lbl: CSSProperties = { fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginBottom: 5 };
  const SOURCE = {
    base: { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', txt: t('réglage de cet écran') },
    env: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', txt: t('configuration serveur (.env)') },
    aucun: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', txt: t('aucun envoi possible') },
  }[data.effective];

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--ink-200)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Messagerie sortante')}</span>
          {/* Ce qui S'APPLIQUE, avec sa source — jamais ce qui est simplement enregistré. */}
          <span style={{
            height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: SOURCE.bg, color: SOURCE.fg,
            fontSize: 'var(--t-meta)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
          }}>{t('en vigueur : {txt}', { txt: SOURCE.txt })}</span>
        </div>
        <p style={{ margin: '4px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.55, maxWidth: 660 }}>
          <Trans k='D’où partent les e-mails du back-office — aujourd’hui la réinitialisation de mot de passe. Réglable ici pour changer de fournisseur sans redéploiement ; la configuration serveur reste le <1>filet</1> si ce réglage est incomplet.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        {data.note && (
          <p style={{ margin: '6px 0 0', fontSize: 'var(--t-body)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>⚠️ {t(data.note)}</p>
        )}
      </div>

      <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '13px 16px' }}>
        <div>
          <div style={lbl}>{t('Transport')}</div>
          <div style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', overflow: 'hidden' }}>
            {([['smtp', 'SMTP'], ['mailjet', t('Mailjet')], ['none', t('Aucun')]] as const).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, transport: k })} style={{
                height: 36, padding: '0 14px', border: 'none', cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: 700,
                background: f.transport === k ? 'var(--ink-100)' : 'transparent',
                color: f.transport === k ? 'var(--ink-900)' : 'var(--ink-500)',
              }}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <div id={`${fieldId}-2`} style={lbl}>{t('Adresse d’expéditeur')}</div>
          {/* ⚠️ TROIS FORMES IDENTIQUES, ET C'EST UNE DÉCISION — pas un oubli (même logique que la
              Forme DF pour un pluriel). Une ADRESSE ne se traduit pas, et elle ne se distingue pas
              d'un MOTIF par sa forme : `prenom.nom@enseigne.fr` est fait de MOTS et se traduit (il
              dit la forme attendue), celui-ci est une adresse RÉELLE. Aucune règle ne peut les
              séparer ; le sens seul le peut. On la fait donc passer par le résolveur — le plafond
              de l'écran reste à 0, et la décision est ÉCRITE au catalogue plutôt que dans un motif
              de plus que personne ne relirait. */}
          <input aria-labelledby={`${fieldId}-2`} className="field-input" style={{ height: 36, width: '100%' }} value={f.fromEmail ?? ''}
            onChange={(e) => setF({ ...f, fromEmail: e.target.value })} placeholder={t('no-reply@orgazone.fr')} />
        </div>
        <div>
          <div id={`${fieldId}-3`} style={lbl}>{t('Nom affiché')}</div>
          <input aria-labelledby={`${fieldId}-3`} className="field-input" style={{ height: 36, width: '100%' }} value={f.fromName ?? ''}
            onChange={(e) => setF({ ...f, fromName: e.target.value })} placeholder={t('Back-office Orgazone')} />
        </div>

        {f.transport === 'smtp' && (
          <>
            <div>
              <div id={`${fieldId}-4`} style={lbl}>{t('Serveur SMTP')}</div>
              <input aria-labelledby={`${fieldId}-4`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                value={f.smtpHost ?? ''} onChange={(e) => setF({ ...f, smtpHost: e.target.value })} placeholder="ssl0.ovh.net" />
            </div>
            <div>
              <div id={`${fieldId}-5`} style={lbl}>{t('Port')}</div>
              <input aria-labelledby={`${fieldId}-5`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                inputMode="numeric" value={f.smtpPort ?? ''}
                onChange={(e) => setF({ ...f, smtpPort: Number(e.target.value.replace(/\D/g, '')) || null })} placeholder="465" />
            </div>
            <div>
              <div style={lbl}>{t('Chiffrement')}</div>
              <div style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', overflow: 'hidden' }}>
                {([[true, t('SSL/TLS')], [false, t('STARTTLS')]] as const).map(([k, l]) => (
                  <button key={String(k)} type="button" onClick={() => setF({ ...f, smtpSecure: k })} style={{
                    height: 36, padding: '0 14px', border: 'none', cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: 700,
                    background: (f.smtpSecure !== false) === k ? 'var(--ink-100)' : 'transparent',
                    color: (f.smtpSecure !== false) === k ? 'var(--ink-900)' : 'var(--ink-500)',
                  }}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div id={`${fieldId}-0`} style={lbl}>{t('Identifiant')}</div>
              <input aria-labelledby={`${fieldId}-0`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                value={f.smtpUser ?? ''} onChange={(e) => setF({ ...f, smtpUser: e.target.value })} />
            </div>
          </>
        )}

        {f.transport === 'mailjet' && (
          <div>
            <div id={`${fieldId}-1`} style={lbl}>{t('Clé API')}</div>
            <input aria-labelledby={`${fieldId}-1`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
              value={f.apiKey ?? ''} onChange={(e) => setF({ ...f, apiKey: e.target.value })} />
          </div>
        )}

        {f.transport !== 'none' && (
          <div>
            <div id={`${fieldId}-6`} style={lbl}>{f.transport === 'mailjet' ? t('Secret API') : t('Mot de passe')}</div>
            <input aria-labelledby={`${fieldId}-6`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
              type="password" autoComplete="new-password" value={f.secret ?? ''}
              onChange={(e) => setF({ ...f, secret: e.target.value })}
              placeholder={data.secretSetAt ? t('•••••••• (inchangé)') : t('à renseigner')} />
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 4, lineHeight: 1.45 }}>
              {data.secretSetAt
                ? t('Défini le {v} — laisser vide pour le conserver.', { v: new Date(data.secretSetAt).toLocaleDateString('fr-FR') })
                : t('Jamais défini.')}
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '12px 18px', borderTop: '1px solid var(--ink-200)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* ⚠️ NOMMÉ PAR CE QU'IL ENREGISTRE : la page porte deux « Enregistrer » (les paramètres de caisse au-dessus) — le texte
            visible reste en tête du nom. */}
        <button type="button" className="btn-cta" disabled={busy} aria-label={t('Enregistrer la messagerie')} onClick={() => { void save(); }}>
          {busy ? t('Enregistrement…') : t('Enregistrer')}
        </button>
        <span aria-hidden="true" style={{ width: 1, height: 24, background: 'var(--ink-200)' }} />
        {/* ⚠️ L'ESSENTIEL de cette carte : on SAIT que ça marche, au lieu de le supposer. */}
        <input className="field-input" style={{ height: 36, width: 230 }} value={testTo}
          onChange={(e) => setTestTo(e.target.value)} placeholder={t('Envoyer un test à…')} />
        <button type="button" className="btn-ghost"
          disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(testTo.trim())}
          onClick={() => { void tester(); }}>{t('Envoyer un test')}</button>
        {testRes && (
          <span style={{ fontSize: 'var(--t-body)', color: testRes.ok ? 'var(--ok-fg)' : 'var(--danger-ink)', lineHeight: 1.45, minWidth: 0 }}>
            {testRes.ok ? t('✓ Message parti.') : `✕ ${testRes.error ?? t('échec')}`}
          </span>
        )}
      </div>

      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--ink-200)', fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        <Trans k='⚠️ Ce réglage ne concerne <1>que le back-office</1>. La messagerie du fournisseur d’identité (connexion SSO) garde sa propre configuration serveur, à dessein : qui contrôle son expéditeur contrôle les réinitialisations de mot de passe de tous les comptes.' parts={[(x) => <strong>{x}</strong>]} /></div>
    </div>
  );
}
